// Ruleset metadata + FAQ content. Single source of truth for anything that
// needs to show users a human-readable description of a ruleset. Consumed by
// the HomePage picker tooltip, the lobby badge, and the /rules FAQ page.
//
// Keep this purely data-shaped (no JSX) so it's importable from anywhere
// without pulling React into the dependency graph.

import type { Ruleset } from "../types/game";

export interface RuleSection {
  heading: string;
  body: string[];
}

export interface RulesetInfo {
  id: Ruleset;
  label: string;          // "Tournament Rules"
  shortLabel: string;     // "Tournament"
  tagline: string;        // one-liner for the picker
  summary: string;        // 1–2 sentence summary for tooltips
  sections: RuleSection[];
}

// ------------------------------------------------------------
// Shared background (identical regardless of ruleset)
// ------------------------------------------------------------

const SHARED_BASICS: RuleSection[] = [
  {
    heading: "The table",
    body: [
      "Four players in two fixed partnerships. Partners sit across from each other — seats 0 & 2 form Team 1, seats 1 & 3 form Team 2.",
      "A standard 52-card deck. Each hand, every player is dealt 6 cards. The rest of the deck is not used.",
    ],
  },
  {
    heading: "Deal & turn order",
    body: [
      "The dealer rotates clockwise after every hand. The first dealer of a new match is random.",
      "Action moves clockwise around the table. The player to the dealer's left bids first.",
    ],
  },
  {
    heading: "Bidding",
    body: [
      "Each player, in turn, bids 2, 3, 4, or passes. A bid must be higher than any previous bid that hand.",
      "If all four players pass, the dealer is forced to bid 2 (\"stuck\").",
      "The highest bidder picks trump and leads the first trick. The first lead must be trump.",
    ],
  },
  {
    heading: "Following suit & trump",
    body: [
      "You must follow the suit led if you can — EXCEPT you may always play a trump instead, even when you could have followed.",
      "If you cannot follow suit, you may play any card (including trump).",
    ],
  },
  {
    heading: "Scoring a hand",
    body: [
      "Four points are available each hand:",
      "• HIGH — highest trump dealt (always scored)",
      "• LOW — lowest trump dealt (always scored)",
      "• JACK — team that captured the Jack of trump, if it was in play",
      "• GAME — team with the most card-point total (10 = 10, A = 4, K = 3, Q = 2, J = 1; other cards = 0). Tie = no point.",
      "If the bidding team earns at least their bid, each team keeps what it earned. If the bidding team falls short (\"goes set\"), they LOSE points equal to their bid instead.",
    ],
  },
];

// ------------------------------------------------------------
// TPC Tournament Rules
// ------------------------------------------------------------

const TPC_DISTINCTIVES: RuleSection[] = [
  {
    heading: "Winning the match",
    body: [
      "First team to 11 points wins. If both teams reach 11 on the same hand, the bidding team wins.",
      "Either team can go out on any hand, regardless of who bid.",
    ],
  },
];

// ------------------------------------------------------------
// Vacation rules
// ------------------------------------------------------------

const VACATION_DISTINCTIVES: RuleSection[] = [
  {
    heading: "Coke hand",
    body: [
      "If your six-card hand contains only cards ranked 3–9 (no 2, no 10, no face cards, no Aces), you may declare a \"coke hand\".",
      "When it's your turn to bid, a \"Claim coke hand\" button appears instead of your normal options. Clicking it replaces your six cards with six fresh ones drawn from the undealt portion of the deck.",
      "You forfeit your right to bid this hand — the engine auto-passes for you. You play out the hand with your new cards normally.",
      "If EVERY seat passes or claims coke (including the dealer), the hand is abandoned: no points scored, dealer rotates, and the next hand is dealt.",
    ],
  },
  {
    heading: "Winning the match",
    body: [
      "First team to 11 wins — but ONLY on a hand their own team bid. If you cross 11 as the non-bidding team, your score clamps at 10; you can't go out that way.",
      "The bidding team can still go set at 10 — they just lose their bid value and stay/fall back below 11.",
    ],
  },
];

// ------------------------------------------------------------
// Exported metadata
// ------------------------------------------------------------

export const RULESET_INFO: Record<Ruleset, RulesetInfo> = {
  tpc: {
    id: "tpc",
    label: "Tournament Rules",
    shortLabel: "Tournament",
    tagline: "Standard Setback rules. Trump anytime, first to 11.",
    summary:
      "Competition-style Setback. Trump anytime. First team to 11 wins; the bidding team wins ties.",
    sections: [...SHARED_BASICS, ...TPC_DISTINCTIVES],
  },
  vacation: {
    id: "vacation",
    label: "Vacation rules",
    shortLabel: "Vacation",
    tagline: "Trump anytime, coke redeals, must bid to go out.",
    summary:
      "House variant with two twists on Tournament rules: \"coke hand\" redeals for 3–9 trash hands, and you can only go out as the bidder.",
    sections: [...SHARED_BASICS, ...VACATION_DISTINCTIVES],
  },
};

// Ordered list for iteration in pickers / FAQ page.
export const RULESET_LIST: RulesetInfo[] = [
  RULESET_INFO.tpc,
  RULESET_INFO.vacation,
];
